import { Checkbox, Field, Input, Text, tokens } from '@fluentui/react-components';
import { useTranslation } from '../../i18n';
import { runLabel } from '../../utils/reviewMonths';
import { isUsable, yearToDate, type StatementChoice } from '../../utils/statementPeriod';

interface Props {
  /** The review's period end; the default is year-to-date of that year. */
  reviewPeriodEnd: string;
  value: StatementChoice;
  onChange: (next: StatementChoice) => void;
  disabled?: boolean;
}

/**
 * "Statements cover" on Send to Client (spec §10.13.4). The firm sends year-to-date, so that is the default and
 * it is stated, never hidden. A different range, across years included, is unlocked ONLY by the explicit
 * "These reports cover a different period" check; unticking it snaps back to year-to-date.
 */
export function StatementPeriodField({ reviewPeriodEnd, value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const ytd = yearToDate(reviewPeriodEnd);
  const usable = isUsable(value);

  return (
    <div data-testid="statement-period">
      <Field label={t('sendEmail.statement.label')}>
        <Text>
          {value.custom
            ? usable
              ? runLabel(value.start, value.end)
              : '—'
            : t('sendEmail.statement.ytd', { range: ytd.start && ytd.end ? runLabel(ytd.start, ytd.end) : '—' })}
        </Text>
      </Field>
      <Checkbox
        checked={value.custom}
        disabled={disabled}
        label={t('sendEmail.statement.different')}
        onChange={(_, d) => onChange(d.checked ? { ...ytd, custom: true } : ytd)}
      />
      {value.custom && (
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' }}>
          <Field label={t('sendEmail.statement.first')} required>
            <Input type="month" value={value.start} disabled={disabled} onChange={(_, d) => onChange({ ...value, start: d.value })} />
          </Field>
          <Field
            label={t('sendEmail.statement.last')}
            required
            validationMessage={usable ? undefined : t('sendEmail.statement.invalid')}
          >
            <Input type="month" value={value.end} disabled={disabled} onChange={(_, d) => onChange({ ...value, end: d.value })} />
          </Field>
        </div>
      )}
      <Text block size={200} style={{ color: tokens.colorNeutralForeground3 }}>
        {t('sendEmail.statement.note')}
      </Text>
    </div>
  );
}
